import {
	ChangeDetectionStrategy,
	Component,
	computed,
	input,
	InputSignal,
	OnChanges,
	OnDestroy,
	OnInit,
	output,
	OutputEmitterRef,
	Signal,
	signal,
	SimpleChanges,
	WritableSignal,
	inject,
} from '@angular/core'
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { SlTpValuesEnum } from '@modules/stocks-module/orders/enums/sl-tp-values.enum'
import { SlTpValuesInterface } from '@modules/stocks-module/orders/interfaces/sl-tp-values.interface'
import { SlTpType } from '@shared/components/trading/orders/common/sl-tp-controls/core/types/sl-tp.type'
import { Subject, takeUntil } from 'rxjs'
import { TuiCheckbox, TuiInputNumber } from '@taiga-ui/kit'
import { TranslatePipe } from '@ngx-translate/core'
import { TuiNumberFormat, TuiTextfield, TuiTitle } from '@taiga-ui/core'
import { SlTpInterface } from '@modules/stocks-module/orders/interfaces/sl-tp.interface'
import { GridsterService } from '@shared/services/gridster.service'
import { UtilsService } from '@shared/services/utils.service'
import { UseLocales } from '@modules/lang-module/decorators/use-locales'
import ru from './core/locale/ru.json'
import en from './core/locale/en.json'
import { SlTpEnum } from '@modules/stocks-module/orders/enums/sl-tp.enum'
import { StocksOrdersHelperService } from '@modules/stocks-module/orders/services/stocks-orders-helper.service'
import { SlTpBoundsInterface } from './core/interfaces/bound-controls.interface'
import { InputNumberSignsDirective } from '@shared/directives/input-number-signs.directive'

@UseLocales({ ru, en })
@Component({
	selector: 'app-sl-tp-controls',
	templateUrl: './sl-tp-controls.component.html',
	styleUrl: './sl-tp-controls.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		ReactiveFormsModule,
		FormsModule,
		TuiCheckbox,
		TranslatePipe,
		TuiTextfield,
		TuiTitle,
		TuiInputNumber,
		TuiNumberFormat,
		InputNumberSignsDirective,
	],
})
export class SlTpControlsComponent implements OnInit, OnChanges, OnDestroy {
	protected readonly defaultSlTpBoundary: number = 0.01
	protected readonly infinity: number = Infinity
	protected readonly object: ObjectConstructor = Object
	protected readonly slTpEnum: typeof SlTpEnum = SlTpEnum
	protected readonly slTpValuesEnum: typeof SlTpValuesEnum = SlTpValuesEnum

	public qty: InputSignal<number> = input<number>(0)
	public targetPrice: InputSignal<number> = input<number>(0)
	public isEnableBounds: InputSignal<boolean> = input<boolean>(true)
	public isBuy: InputSignal<boolean> = input<boolean>(true)
	public allowToggle: InputSignal<boolean> = input<boolean>(true)
	public defaultCalcField: InputSignal<SlTpValuesEnum> = input<SlTpValuesEnum>(this.slTpValuesEnum.PERCENT)

	public isActiveStopLoss: InputSignal<boolean> = input<boolean>(false)
	public defaultPercentStopLoss: InputSignal<number> = input<number>(-1)
	public defaultPriceStopLoss: InputSignal<number> = input<number>(0)

	public isActiveTakeProfit: InputSignal<boolean> = input<boolean>(false)
	public defaultPercentTakeProfit: InputSignal<number> = input<number>(1)
	public defaultPriceTakeProfit: InputSignal<number> = input<number>(0)

	public changeActiveStopLoss: OutputEmitterRef<boolean> = output<boolean>()
	public changePriceStopLoss: OutputEmitterRef<number> = output<number>()

	public changeActiveTakeProfit: OutputEmitterRef<boolean> = output<boolean>()
	public changePriceTakeProfit: OutputEmitterRef<number> = output<number>()

	private _destroy$: Subject<void> = new Subject<void>()

	private readonly _formBuilder: FormBuilder = inject<FormBuilder>(FormBuilder)
	private readonly _ordersStocksHelperService: StocksOrdersHelperService =
		inject<StocksOrdersHelperService>(StocksOrdersHelperService)
	private readonly _gridsterService: GridsterService = inject<GridsterService>(GridsterService)
	private readonly _utilsService: UtilsService = inject<UtilsService>(UtilsService)

	protected stopLossForm: FormGroup | null = null
	protected takeProfitForm: FormGroup | null = null

	protected isOpenSlTpDropdown: WritableSignal<SlTpInterface<boolean>> = signal({
		stopLoss: false,
		takeProfit: false,
	})

	protected slTpBounds: Signal<SlTpInterface<SlTpBoundsInterface> | null> = computed(
		(): SlTpInterface<SlTpBoundsInterface> | null => {
			if (!this.isEnableBounds()) return null

			return {
				stopLoss: {
					min: {
						percent: null,
						price: this.getMinPrice(this.slTpEnum.STOP_LOSS),
						dollar: null,
					},
					max: {
						percent: -this.defaultSlTpBoundary,
						price: this.getMaxPrice(this.slTpEnum.STOP_LOSS),
						dollar: -this.defaultSlTpBoundary,
					},
				},
				takeProfit: {
					min: {
						percent: this.defaultSlTpBoundary,
						price: this.getMinPrice(this.slTpEnum.TAKE_PROFIT),
						dollar: this.defaultSlTpBoundary,
					},
					max: {
						percent: null,
						price: this.getMaxPrice(this.slTpEnum.TAKE_PROFIT),
						dollar: null,
					},
				},
			}
		},
	)
	private _getGroupControls(): FormGroup {
		return this._formBuilder.group({
			isActive: [false],
			values: this._formBuilder.group({
				percent: [0],
				price: [0],
				dollar: [0],
			}),
		})
	}

	private _initForms(): void {
		this.stopLossForm = this._getGroupControls()
		this.takeProfitForm = this._getGroupControls()
	}

	private _fillForms(): void {
		this.stopLossForm?.patchValue({
			isActive: this.isActiveStopLoss(),
		})

		this.takeProfitForm?.patchValue({
			isActive: this.isActiveTakeProfit(),
		})

		this.toggleActiveSlTp('stop-loss')
		this.toggleActiveSlTp('take-profit')
	}

	private _watchSlTpValues(type: SlTpType): void {
		const values: AbstractControl | null | undefined =
			type === 'stop-loss' ? this.stopLossForm?.get('values') : this.takeProfitForm?.get('values')
		if (!(values instanceof FormGroup)) return
		this.object.keys(values.controls).forEach((field: string): void => {
			values
				.get(field)
				?.valueChanges.pipe(takeUntil(this._destroy$))
				.subscribe((): void => {
					this.calcSlTp(type, field as SlTpValuesEnum)
				})
		})
	}

	private _listenDragAndResize(): void {
		this._gridsterService.isDragAndResize$.pipe(takeUntil(this._destroy$)).subscribe((value: boolean): void => {
			if (value) this.onClose()
		})
	}

	protected getMinPrice(slTpType: SlTpEnum): number {
		if (slTpType === this.slTpEnum.STOP_LOSS) {
			return this.isBuy() ? 1 : this.targetPrice()
		} else {
			return this.isBuy() ? this.targetPrice() : 1
		}
	}

	protected getMaxPrice(slTpType: SlTpEnum): number {
		if (slTpType === this.slTpEnum.STOP_LOSS) {
			return this.isBuy() ? this.targetPrice() : this.infinity
		} else {
			return this.isBuy() ? this.infinity : this.targetPrice()
		}
	}

	protected putCursorEnd(input: HTMLInputElement): void {
		this._utilsService.putCursorEnd(input)
	}

	protected getSlTpValues(type: SlTpType, value: SlTpValuesEnum): number {
		const form: FormGroup | null = type === 'stop-loss' ? this.stopLossForm : this.takeProfitForm
		return form?.get('values')?.get(value)?.value
	}

	protected toggleActiveSlTp(type: SlTpType): void {
		const form: FormGroup | null = type === 'stop-loss' ? this.stopLossForm : this.takeProfitForm
		if (!form) return

		const isActive: boolean = !!form.get('isActive')?.value
		const outputActive: OutputEmitterRef<boolean> =
			type === 'stop-loss' ? this.changeActiveStopLoss : this.changeActiveTakeProfit
		const percent: number = type === 'stop-loss' ? this.defaultPercentStopLoss() : this.defaultPercentTakeProfit()
		const price: number = type === 'stop-loss' ? this.defaultPriceStopLoss() : this.defaultPriceTakeProfit()

		if (!isActive) {
			form.get('values')?.disable()
			form.get('values')?.patchValue({ percent: 0, price: 0, dollar: 0 }, { emitEvent: false })
		} else {
			form.get('values')?.enable()
			if (this.defaultCalcField() === this.slTpValuesEnum.PRICE) {
				form.patchValue({ values: { price } }, { emitEvent: false })
			} else {
				form.patchValue({ values: { percent } }, { emitEvent: false })
			}
			this.calcSlTp(type, this.defaultCalcField())
		}
		outputActive.emit(isActive)
	}

	protected calcSlTp(type: SlTpType, field: SlTpValuesEnum): void {
		const form: FormGroup | null = type === 'stop-loss' ? this.stopLossForm : this.takeProfitForm
		if (!form) return

		const outputActive: OutputEmitterRef<number> =
			type === 'stop-loss' ? this.changePriceStopLoss : this.changePriceTakeProfit
		if (!form?.get('isActive')?.value) return
		const value: number = form.get('values')?.get(field)?.value ?? 0

		const calculatedSlTp: SlTpValuesInterface = this._ordersStocksHelperService.calcSlTp(
			this.isBuy(),
			this.qty(),
			this.targetPrice(),
			field,
			value,
		)

		form.patchValue({ values: calculatedSlTp }, { emitEvent: false })
		outputActive.emit(calculatedSlTp.price ?? 0)
	}

	protected onClose(): void {
		this.isOpenSlTpDropdown.set({
			stopLoss: false,
			takeProfit: false,
		})
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['isBuy'] || changes['qty'] || !!changes['targetPrice']) {
			this.calcSlTp('stop-loss', this.slTpValuesEnum.PERCENT)
			this.calcSlTp('take-profit', this.slTpValuesEnum.PERCENT)
		}
		if (changes['isActiveStopLoss']) {
			this.stopLossForm?.patchValue({ isActive: this.isActiveStopLoss() })
			this.toggleActiveSlTp('stop-loss')
		}
		if (changes['isActiveTakeProfit']) {
			this.takeProfitForm?.patchValue({ isActive: this.isActiveTakeProfit() })
			this.toggleActiveSlTp('take-profit')
		}
	}

	ngOnInit(): void {
		this._initForms()
		this._fillForms()
		this._listenDragAndResize()
		this._watchSlTpValues('stop-loss')
		this._watchSlTpValues('take-profit')
	}

	ngOnDestroy(): void {
		this._destroy$.next()
		this._destroy$.complete()
	}
}
